/**
 * iMed — product page (PDP) logic.
 * - Gallery: click-to-swap thumbnails (all rendered, scrollable track) + desktop arrow
 *   buttons + mobile swipe on the main image, all funneling through one goTo(index).
 * - Quantity stepper (-/input/+).
 * - Wishlist heart: real persistence via localStorage (key `imed_wishlist`, array of
 *   product handles) — no accounts/server. Shared format with assets/imed-wishlist.js.
 * - Variant picker (only present when the product has real options): matches the selected
 *   option pills to a variant and updates price, stock message, Add to cart button and
 *   (when available) the main image — all from a JSON blob rendered server-side so no money
 *   math happens in JS.
 * - Sticky mobile add-to-cart bar: shown only once the main buy-box button scrolls out of
 *   view, hidden again once the footer comes into view (IntersectionObserver).
 * - Related products: product-recommendations.liquid now renders synchronously (same
 *   department as this product) in the vast majority of cases, so initRecommendations() below
 *   is a no-op then (no data-url on the container). It only actually fetches for the rare
 *   product with no department yet, where the section falls back to Shopify's own
 *   recommendation engine via the Section Rendering API — same DOMParser idiom as
 *   imed-cart.js's drawer refresh.
 */
(function () {
  'use strict';

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------------- Gallery ----------------
     Every image gets a real thumbnail (no more 4-item cap + "+N" cycle
     button — imed-product.css makes the thumb row a scrollable track).
     goTo(index) is the single source of truth: swaps the main image,
     re-marks the active thumb, scrolls it into view, and wraps around at
     either end — used by thumb clicks, the desktop arrow buttons AND the
     mobile swipe gesture below so all three stay in sync. */
  function initGallery() {
    var galleryJsonEl = qs('[data-gallery-json]');
    if (!galleryJsonEl) return;
    var images;
    try {
      images = JSON.parse(galleryJsonEl.textContent);
    } catch (e) {
      return;
    }
    if (!images.length) return;

    var current = 0;
    qsa('[data-gallery-thumb]').forEach(function (t, i) {
      if (t.classList.contains('is-active')) current = i;
    });

    function goTo(index) {
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      current = index;
      var item = images[current];
      var mainImg = qs('[data-gallery-main] img');
      if (item && mainImg) {
        mainImg.src = item.url;
        mainImg.removeAttribute('srcset');
        mainImg.alt = item.alt || '';
      }
      var thumbs = qsa('[data-gallery-thumb]');
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === current);
      });
      var activeThumb = thumbs[current];
      if (activeThumb && activeThumb.scrollIntoView) {
        activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    qsa('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(parseInt(thumb.getAttribute('data-media-index'), 10));
      });
    });

    var thumbsTrack = qs('[data-gallery-thumbs]');
    qsa('[data-gallery-arrow]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!thumbsTrack) return;
        var dir = btn.getAttribute('data-gallery-arrow') === 'next' ? 1 : -1;
        var firstThumb = qs('[data-gallery-thumb]');
        var step = firstThumb ? (firstThumb.offsetWidth + 10) * 2 : 172;
        thumbsTrack.scrollBy({ left: dir * step, behavior: 'smooth' });
      });
    });

    // Swipe on the main image — touch devices only (mousedown/up never fire
    // touchstart/touchend), so this never interferes with the hover-zoom
    // above or with thumb/arrow clicks on desktop.
    var mainEl = qs('[data-gallery-main]');
    if (mainEl && images.length > 1) {
      var touchStartX = null;
      mainEl.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].clientX;
      }, { passive: true });
      mainEl.addEventListener('touchend', function (e) {
        if (touchStartX === null) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(dx) < 40) return; // ignore taps / tiny jitters
        goTo(dx < 0 ? current + 1 : current - 1);
      }, { passive: true });
    }
  }

  /* ---------------- Quantity stepper ----------------
     Multiple stepper widgets can exist for the same product (the main
     buy-box one + the mobile sticky bar one). They share a
     [data-qty-group] value (the product id) and are kept in sync: changing
     one immediately mirrors the value into every sibling input so whichever
     Add to cart button the shopper clicks reads the same quantity. */
  function syncQtyGroup(group, value, sourceInput) {
    if (!group) return;
    qsa('[data-qty-wrap][data-qty-group="' + group + '"]').forEach(function (wrap) {
      var input = qs('input', wrap);
      if (input && input !== sourceInput) input.value = value;
    });
  }

  function initQuantity() {
    qsa('[data-qty-wrap]').forEach(function (wrap) {
      var input = qs('input', wrap);
      var group = wrap.getAttribute('data-qty-group');
      var minusBtn = qs('[data-qty-minus]', wrap);
      var plusBtn = qs('[data-qty-plus]', wrap);
      if (!input) return;
      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          var val = (parseInt(input.value, 10) || 1) - 1;
          if (val < 1) val = 1;
          input.value = val;
          syncQtyGroup(group, val, input);
        });
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          var val = (parseInt(input.value, 10) || 1) + 1;
          input.value = val;
          syncQtyGroup(group, val, input);
        });
      }
      input.addEventListener('change', function () {
        var val = parseInt(input.value, 10);
        if (!val || val < 1) val = 1;
        input.value = val;
        syncQtyGroup(group, val, input);
      });
    });
  }

  /* ---------------- Wishlist ----------------
     Client-side only, no accounts/server: localStorage key `imed_wishlist`
     holds a plain array of product handles. assets/imed-wishlist.js (the
     /pages/lista-zelja page) reads the exact same key/format — keep the two
     in sync if this ever changes. */
  var WISHLIST_KEY = 'imed_wishlist';

  function getWishlist() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function setWishlist(arr) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr));
    } catch (e) {
      // localStorage unavailable (private mode / disabled) — fail silently,
      // the button still toggles visually for this page view.
    }
  }

  function initWishlist() {
    var btn = qs('[data-wishlist-toggle]');
    if (!btn) return;
    var handle = btn.getAttribute('data-wishlist-handle');
    if (!handle) return;
    var label = qs('[data-wishlist-label]', btn);
    var link = qs('[data-wishlist-link]');

    function render(active) {
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (label) label.textContent = active ? 'Sačuvano u listi želja' : 'Dodaj u listu želja';
      if (link) link.hidden = !active;
    }

    render(getWishlist().indexOf(handle) !== -1);

    btn.addEventListener('click', function () {
      var list = getWishlist();
      var idx = list.indexOf(handle);
      var nowActive = idx === -1;
      if (nowActive) {
        list.push(handle);
      } else {
        list.splice(idx, 1);
      }
      setWishlist(list);
      render(nowActive);
    });
  }

  /* ---------------- Variant picker ---------------- */
  function priceHtml(v, size) {
    var cls = 'imed-price imed-price--' + (size || 'lg');
    var html = '<div class="' + cls + '">';
    if (v.onSale) {
      html += '<span class="imed-price__sale">' + v.priceFormatted + '</span>';
      html += '<span class="imed-price__was">' + v.compareAtFormatted + '</span>';
      if (v.savingsFormatted) {
        html += '<span class="imed-price__savings">Ušteda ' + v.savingsFormatted + '</span>';
      }
    } else {
      html += '<span class="imed-price__regular">' + v.priceFormatted + '</span>';
    }
    html += '</div>';
    return html;
  }

  function updatePriceBlock(v) {
    var el = qs('[data-price-block]');
    if (!el) return;
    el.innerHTML = priceHtml(v, 'lg');
  }

  function updateStock(available) {
    var el = qs('[data-stock-msg]');
    if (!el) return;
    var tpl = available ? qs('[data-stock-template-available]') : qs('[data-stock-template-unavailable]');
    if (tpl) el.innerHTML = tpl.innerHTML;
    el.classList.toggle('imed-pdp__stock--available', !!available);
    el.classList.toggle('imed-pdp__stock--unavailable', !available);
  }

  function updateAddToCart(v) {
    var root = qs('[data-pdp-root]');
    var productId = root ? root.getAttribute('data-product-id') : null;
    if (!productId) return;
    // Updates BOTH the buy-box button and the mobile sticky-bar button —
    // they share data-pdp-add-to-cart="<product id>".
    qsa('[data-pdp-add-to-cart="' + productId + '"]').forEach(function (btn) {
      btn.setAttribute('data-variant-id', v.id);
      btn.disabled = !v.available;
      btn.classList.toggle('imed-btn--primary', !!v.available);
      btn.classList.toggle('imed-btn--disabled', !v.available);
      var label = qs('span', btn);
      if (label) label.textContent = v.available ? 'Dodaj u korpu' : 'Nema na stanju';
    });
  }

  function updateScarcity(v) {
    var el = qs('[data-pdp-scarcity]');
    if (!el) return;
    var show = v.inventoryTracked === 'shopify' && v.inventoryQuantity > 0 && v.inventoryQuantity <= 3;
    el.hidden = !show;
    if (show) {
      var textEl = qs('[data-pdp-scarcity-text]', el);
      if (textEl) textEl.textContent = 'Još ' + v.inventoryQuantity + ' kom na stanju';
    }
  }

  function updateSku(v) {
    var row = qs('[data-details-sku-row]');
    var valueEl = qs('[data-details-sku-value]');
    if (!row || !valueEl) return;
    if (v.sku) {
      valueEl.textContent = v.sku;
      row.hidden = false;
    } else {
      row.hidden = true;
    }
  }

  function updateBadge(v) {
    var badge = qs('[data-gallery-badge]');
    if (!badge) return;
    if (v.onSale) {
      badge.hidden = false;
      badge.textContent = '-' + v.discountPct + '%';
    } else {
      badge.hidden = true;
    }
  }

  function updateMainImageFromVariant(v) {
    if (!v.featuredImage) return;
    var img = qs('[data-gallery-main] img');
    if (!img) return;
    img.src = v.featuredImage;
    img.removeAttribute('srcset');
    if (v.featuredImageAlt) img.alt = v.featuredImageAlt;
  }

  function initVariantPicker() {
    var wrap = qs('[data-variant-picker]');
    if (!wrap) return;
    var dataEl = qs('[data-variants-json]');
    if (!dataEl) return;
    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var selected = [];
    try {
      selected = JSON.parse(wrap.getAttribute('data-selected-options') || '[]');
    } catch (e) {
      selected = [];
    }

    function findVariant() {
      var match = null;
      variants.forEach(function (v) {
        if (match) return;
        if (!v.options || v.options.length !== selected.length) return;
        var same = v.options.every(function (val, i) { return val === selected[i]; });
        if (same) match = v;
      });
      return match;
    }

    function applyVariant(v) {
      if (!v) return;
      updatePriceBlock(v);
      updateStock(v.available);
      updateAddToCart(v);
      updateBadge(v);
      updateMainImageFromVariant(v);
      updateSku(v);
      updateScarcity(v);
    }

    qsa('[data-option-pill]', wrap).forEach(function (pill) {
      pill.addEventListener('click', function () {
        var idx = parseInt(pill.getAttribute('data-option-index'), 10);
        var val = pill.getAttribute('data-option-value');
        selected[idx] = val;
        wrap.setAttribute('data-selected-options', JSON.stringify(selected));
        qsa('[data-option-pill][data-option-index="' + idx + '"]', wrap).forEach(function (p) {
          p.classList.toggle('is-selected', p === pill);
        });
        applyVariant(findVariant());
      });
    });
  }

  /* ---------------- Mobile sticky add-to-cart bar ----------------
     Kept hidden (see imed-product.css .imed-pdp__stickybar) until the
     shopper has scrolled the main "Dodaj u korpu" button (in the buy-box)
     out of view, and hidden again once the site footer scrolls into view —
     otherwise a bar that's fixed+visible from the very first paint covers
     the title on load, and a bar that's fixed+visible all the way to the
     bottom of the page covers the footer.

     BUG FIXED HERE (2026-09-01): this used to run purely on two
     IntersectionObservers (threshold 0) watching the CTA and the footer.
     IntersectionObserver only fires when an element's intersection ratio
     actually CROSSES a registered threshold. A fast scroll/fling (or any
     single big jump — confirmed via window.scrollTo, but momentum
     scrolling on a real phone can do the same) can move the CTA straight
     from "fully below the viewport" (ratio 0) to "fully above the
     viewport" (ratio 0) between two rendered frames, without the browser
     ever sampling it as on-screen. Ratio 0 -> ratio 0 crosses no
     threshold, so the callback never fires, `primaryCtaVisible` stays
     stuck at its last value (true, from the initial "below the fold"
     read) and the bar never appears — reproduced live with Chromium
     devtools + a real navigator (page load then window.scrollTo straight
     past the CTA): scrollY landed 600px past the button, footer nowhere
     near, yet the observer log showed zero callbacks after the jump.
     Fixed by dropping IntersectionObserver for this bar entirely and
     using the same scroll+rAF+getBoundingClientRect pattern already
     proven below in initAssistantFabGuard() — a real "scroll" event fires
     on every frame of a scroll/fling (no threshold-crossing to miss), so
     visibility is recomputed directly from current geometry every time. */
  function initStickyBar() {
    var bar = qs('[data-pdp-stickybar]');
    var primaryCta = qs('[data-pdp-primary-cta]');
    var footer = qs('.imed-footer');
    if (!bar || !primaryCta) return;

    var fab = document.getElementById('imedAssistantFab');

    function computeVisible() {
      // Past the CTA: its bottom edge has scrolled above the top of the
      // viewport (the shopper scrolled past it). While it's still below
      // the fold (not reached yet, e.g. a tall gallery on load) this is
      // false too, which is what we want — don't flash the bar in early.
      var ctaPassed = primaryCta.getBoundingClientRect().bottom < 0;
      var footerVisible = false;
      if (footer) {
        var r = footer.getBoundingClientRect();
        var viewportH = window.innerHeight || document.documentElement.clientHeight;
        footerVisible = r.top < viewportH && r.bottom > 0;
      }
      return ctaPassed && !footerVisible;
    }

    function sync() {
      var visible = computeVisible();
      bar.classList.toggle('is-visible', visible);
      // Mirror the bar's visibility onto Mila's FAB so it only lifts above
      // the sticky "Dodaj u korpu" bar while that bar actually occupies the
      // bottom of the screen — see initAssistantFabGuard() below for the
      // separate "never sits on top of price/CTA" safeguard.
      if (fab) fab.classList.toggle('is-above-stickybar', visible);
    }

    var ticking = false;
    function onScrollOrResize() {
      if (ticking) return;
      ticking = true;
      (window.requestAnimationFrame || window.setTimeout)(function () {
        ticking = false;
        sync();
      });
    }

    sync(); // synchronous first check — see initAssistantFabGuard()'s note below.
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
  }

  /* ---------------- Mila FAB collision guard ----------------
     The floating "Pitaj Milu" button is position:fixed bottom-right on
     every page, so whatever the shopper has scrolled to is directly behind
     it. On the PDP that is not just the buy card (price, qty, Add to cart,
     trust rows incl. "Pitajte farmaceuta") — it is EVERYTHING from there
     down: the description/disclaimer text, the "Slični proizvodi" product
     grid ("U korpu" buttons + product names) and the footer (links,
     newsletter field). Verified on real WebKit iPhone viewports (14 Pro
     Max/14/SE — not just Chromium's mobile emulation, which was where the
     earlier buy-column-only guard looked "fixed" because that check never
     scrolled past the buy box): a full-opacity fixed circle sitting on top
     of any of that reads as text being sliced/cut off mid-word.
     Fix: once the shopper has scrolled far enough that the buy column's
     top edge has reached (or passed above) the viewport, fade + shrink +
     disable the FAB (pointer-events:none, see imed-assistant.css
     .is-compact) and KEEP it that way for the rest of the page — do not
     pop back to full size just because the buy column itself scrolled out
     of view, since the description/related-products/footer behind it need
     the same protection. Full size/opacity only returns if the shopper
     scrolls back up above the buy column (e.g. back to the hero image). */
  function initAssistantFabGuard() {
    var buyCol = qs('.imed-pdp__buycol');
    var fab = document.getElementById('imedAssistantFab');
    if (!buyCol || !fab) return;

    var ticking = false;

    function check() {
      ticking = false;
      var r = buyCol.getBoundingClientRect();
      var viewportH = window.innerHeight || document.documentElement.clientHeight;
      // Compact from the moment the buy column's top edge is at or above
      // the bottom of the viewport (i.e. it has been reached) all the way
      // through the rest of the page — r.top keeps getting more negative
      // as the shopper scrolls past it, so this stays true past the buy
      // box, past the description card, past related products, into the
      // footer. Only resets to false when scrolled back up so far that the
      // buy column hasn't been reached yet (r.top >= viewportH).
      fab.classList.toggle('is-compact', r.top < viewportH);
    }

    // Synchronous first check: a scroll/resize listener's own callback
    // only fires on the next event, which is imperceptible once the page
    // is actively rendering but would otherwise let the very first paint
    // show the FAB at full size/opacity even when it's already sitting
    // over the buy card (e.g. desktop, where the buy column is tall enough
    // to already be on screen at scroll position 0).
    check();

    function onScrollOrResize() {
      if (ticking) return;
      ticking = true;
      (window.requestAnimationFrame || window.setTimeout)(check);
    }
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
  }

  /* ---------------- Related products ---------------- */
  function initRecommendations() {
    var container = qs('[data-product-recommendations]');
    if (!container) return;
    var url = container.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-product-recommendations]');
        if (!fresh) return;
        container.innerHTML = fresh.innerHTML;
        document.dispatchEvent(new CustomEvent('imed:cards-updated', { detail: { root: container } }));
      })
      .catch(function (err) {
        console.error('iMed recommendations fetch error', err);
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initGallery();
    initQuantity();
    initWishlist();
    initVariantPicker();
    initStickyBar();
    initAssistantFabGuard();
    initRecommendations();
  });
})();
